import * as React from 'react';
import isNil from 'lodash/isNil';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { NewTableProps } from './new-table.types';
import { TableView } from './components/table-view';
import { useController } from './controller';
import { Actions } from './components/actions';
import { FieldSelector } from '@common/components/FieldSelector';
import { Filters } from './components/filters';
import { MessageBox } from '../MessageBox';

export const NewTable: React.FC<NewTableProps> = (props) => {
  const { loadMetadata, noDataMessage, columns, classes, filters, moduleName, columnsManagement, itemUniqProperty, columnsOrder, getId } = props;
  const {
    tableInstance,
    currentActions,
    isOpenedFieldSelector,
    currentContextMenuColumnId,
    areOpenedFilters,
    actions,
    loading,
    currentColumn,
    messageProps,
    rootRef,
    getFieldSelectorFolderIconId,
    getFieldSelectorFolderCollapseIconId,
    getFieldSelectorFolderId,
    getFieldSelectorFieldId,
    closeMessage,
    showMessage,
    applyColumnFilters,
    closeFilters,
    handleLazyLoad,
    handleColumnSizeChange,
    handleRowContextMenu,
    isRowSelected,
    handleRowClick,
    handleSort,
    handleHeaderCellContextMenu,
    getMenuVerticalPosition,
    getMenuHorizontalPosition,
    closeHeaderActions,
    closeFieldSelector,
    handleColumnAdd,
    handleHeaderContextMenu,
    handleColumnsOrderChange
  } = useController(props);

  const tableElement = (
    <TableView
      getId={getId}
      rootRef={rootRef}
      loading={loading}
      classes={classes}
      columns={columns}
      currentColumnResizeOffset={tableInstance.getState().columnSizingInfo.deltaOffset || undefined}
      isRowSelected={isRowSelected}
      itemUniqProperty={itemUniqProperty}
      noDataMessage={noDataMessage}
      model={tableInstance}
      loadTableMetadata={loadMetadata}
      onRowContextMenu={handleRowContextMenu}
      onColumnResizeEnd={handleColumnSizeChange}
      onSort={handleSort}
      onRowClick={handleRowClick}
      onHeaderContextMenu={handleHeaderContextMenu}
      onCellContextMenu={handleHeaderCellContextMenu}
      onLazyLoad={handleLazyLoad}
      onColumnsOrderChange={handleColumnsOrderChange}
    />
  );

  return (
    <>
      <MessageBox
        hideMessageBox={closeMessage}
        {...messageProps}
      />
      {isOpenedFieldSelector && columnsManagement && (
        <FieldSelector 
          withSearch
          getId={{
            search: 'search-box-tx',
            searchIcon: 'search-box-icon',
            title: 'lbl-ambry-whs-header',
            cancelBtn: 'btn-ambry-whs-cancel',
            submitBtn: 'btn-ambry-whs-add',
            field: getFieldSelectorFieldId,
            folder: getFieldSelectorFolderId,
            folderCollapseIcon: getFieldSelectorFolderCollapseIconId,
            folderIcon: getFieldSelectorFolderIconId,
          }}
          getFolders={columnsManagement.getFolders}
          getFields={columnsManagement.getFields}
          onChange={handleColumnAdd}
          close={closeFieldSelector}
        />
      )}
      {areOpenedFilters && filters && currentColumn && (
        <Filters
          showMessage={showMessage}
          moduleName={moduleName}
          filterName={typeof currentColumn?.header === 'number' || typeof currentColumn?.header === 'string'
            ? currentColumn?.header
            : undefined
          }
          defaultValue={loadMetadata?.filtering?.columns && !isNil(currentContextMenuColumnId) 
            ? loadMetadata?.filtering.columns[currentContextMenuColumnId] 
            : undefined}
          filters={filters.operators}
          onClose={closeFilters}
          onApply={applyColumnFilters}
          {...currentColumn?.filter}
        />
      )} 
      {currentActions?.coords && actions && (
        <Actions 
          style={{
            ...getMenuHorizontalPosition(currentActions.coords.x),
            ...getMenuVerticalPosition(currentActions.coords.y),
          }}
          columnId={currentContextMenuColumnId!}
          className="Table__actions"
          items={actions}
          onClose={closeHeaderActions}
        />
      )}
      {columnsOrder?.onChange
        ? (
          <DndProvider backend={HTML5Backend}>
            {tableElement}
          </DndProvider>
        )
        : tableElement
      }
    </>
  );
};

NewTable.defaultProps = {
  loadItemsCount: 45,
};